import React, { useRef, useState } from "react";
import { UploadZone, ResetButton } from "./_shared";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function ColorPicker() {
  const [file, setFile] = useState(null);
  const [src, setSrc] = useState(null);
  const [color, setColor] = useState(null);
  const [history, setHistory] = useState([]);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const handleFile = (f) => { setFile(f); setSrc(URL.createObjectURL(f)); };

  const onLoad = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
  };

  const onClick = (e) => {
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * img.naturalWidth;
    const y = ((e.clientY - rect.top) / rect.height) * img.naturalHeight;
    const ctx = canvasRef.current.getContext("2d");
    const p = ctx.getImageData(x, y, 1, 1).data;
    const hex = "#" + [p[0], p[1], p[2]].map((c) => c.toString(16).padStart(2, "0")).join("").toUpperCase();
    setColor({ hex, rgb: `rgb(${p[0]}, ${p[1]}, ${p[2]})` });
    setHistory((h) => [{ hex, rgb: `rgb(${p[0]}, ${p[1]}, ${p[2]})` }, ...h.filter((c) => c.hex !== hex)].slice(0, 10));
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); };
  const reset = () => { setFile(null); setSrc(null); setColor(null); setHistory([]); };

  if (!file) return <UploadZone onFile={handleFile} />;
  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />
      <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden bg-slate-50 dark:bg-[#0b0c0e] p-4">
        <img ref={imgRef} src={src} alt="src" onLoad={onLoad} onClick={onClick} className="w-full max-h-[500px] object-contain cursor-crosshair" />
      </div>
      {color && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-6 bg-white dark:bg-[#121316] flex items-center gap-4" data-testid="color-result">
          <div className="w-20 h-20 rounded-xl border border-slate-200 dark:border-white/10" style={{ background: color.hex }} />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-slate-900 dark:text-white">{color.hex}</span>
              <button onClick={() => copy(color.hex)} className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400"><Copy size={14} /></button>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-slate-500 dark:text-slate-400">{color.rgb}</span>
              <button onClick={() => copy(color.rgb)} className="text-slate-500 hover:text-blue-600 dark:hover:text-blue-400"><Copy size={14} /></button>
            </div>
          </div>
        </div>
      )}
      {history.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">History</p>
          <div className="flex flex-wrap gap-2">
            {history.map((c, i) => (
              <button key={i} onClick={() => copy(c.hex)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 hover:border-blue-500 text-xs font-mono">
                <span className="w-4 h-4 rounded" style={{ background: c.hex }} /> {c.hex}
              </button>
            ))}
          </div>
        </div>
      )}
      <ResetButton onClick={reset} />
    </div>
  );
}
